import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation
   * and actually deactivating the active item.
   *
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate a menu item from a real event target.
   *
   * @param {Event} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    this.dataset.overflowExpanded = (
      event.target instanceof HTMLElement &&
      event.target.slot !== ''
    ).toString();

    this.#deactivate();

    this.#state.activeItem = item;
    this.setAttribute('aria-expanded', 'true');
    item.setAttribute('aria-expanded', 'true');

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty(
      '--submenu-height',
      `${submenu.offsetHeight}px`
    );
  };

  /**
   * Activate a specific menu item directly.
   *
   * @param {HTMLElement} item
   */
  #activateItem = (item) => {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (item === this.#state.activeItem) return;

    this.#deactivate();

    this.#state.activeItem = item;
    this.setAttribute('aria-expanded', 'true');
    item.setAttribute('aria-expanded', 'true');

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty(
      '--submenu-height',
      `${submenu.offsetHeight}px`
    );
  };

  #debouncedActivateHandler = debounce(
    this.#activateHandler,
    ACTIVATE_DELAY
  );

  /**
   * Deactivate the active item after a delay.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  /**
   * Deactivate the active item after a delay.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item !== this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately.
   *
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    if (!item) return;

    this.#state.activeItem = null;
    this.setAttribute('aria-expanded', 'false');
    item.setAttribute('aria-expanded', 'false');
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Handle keyboard navigation for the desktop mega-menu.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const currentItem = event.target.closest('[role="menuitem"]');

    if (!(currentItem instanceof HTMLElement)) return;

    const topLevelItems = this.#getTopLevelMenuItems();
    const currentIndex = topLevelItems.indexOf(currentItem);
    const submenu = findSubmenu(currentItem);

    switch (event.key) {
      case 'Escape': {
        if (!this.#state.activeItem) return;

        event.preventDefault();
        event.stopPropagation();

        const activeItem = this.#state.activeItem;

        this.#deactivate(activeItem);
        activeItem.focus();

        break;
      }

      case ' ': {
        if (!submenu) return;

        event.preventDefault();
        event.stopPropagation();

        const isExpanded =
          currentItem.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          this.#deactivate(currentItem);
        } else {
          this.#activateItem(currentItem);
        }

        break;
      }

      case 'Enter': {
        if (!submenu) return;

        const isExpanded =
          currentItem.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) {
          event.preventDefault();
          event.stopPropagation();

          this.#activateItem(currentItem);
        }

        break;
      }

      case 'ArrowDown': {
        if (!submenu) return;

        event.preventDefault();
        event.stopPropagation();

        this.#activateItem(currentItem);

        const firstSubmenuItem =
          getFocusableElements(submenu)[0];

        firstSubmenuItem?.focus();

        break;
      }

      case 'ArrowRight': {
        if (currentIndex === -1 || topLevelItems.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const nextIndex =
          (currentIndex + 1) % topLevelItems.length;

        this.#deactivate();
        topLevelItems[nextIndex]?.focus();

        break;
      }

      case 'ArrowLeft': {
        if (currentIndex === -1 || topLevelItems.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const previousIndex =
          (currentIndex - 1 + topLevelItems.length) %
          topLevelItems.length;

        this.#deactivate();
        topLevelItems[previousIndex]?.focus();

        break;
      }

      case 'Home': {
        if (currentIndex === -1 || topLevelItems.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.#deactivate();
        topLevelItems[0]?.focus();

        break;
      }

      case 'End': {
        if (currentIndex === -1 || topLevelItems.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.#deactivate();
        topLevelItems[topLevelItems.length - 1]?.focus();

        break;
      }

      default:
        break;
    }
  };

  /**
   * Get only top-level menu controls.
   *
   * @returns {HTMLElement[]}
   */
  #getTopLevelMenuItems = () => {
    return Array.from(
      this.querySelectorAll(
        '.menu-list__list-item > [role="menuitem"]'
      )
    ).filter(
      (item) =>
        item instanceof HTMLElement &&
        item.closest('header-menu') === this
    );
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu item related to the supplied element.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[role="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  const listItem = element.closest('.menu-list__list-item');

  if (!listItem) return null;

  if (listItem.matches('[slot="more"]')) {
    const overflowItem =
      listItem.parentElement?.querySelector(
        '[slot="overflow"] [role="menuitem"]'
      );

    return overflowItem instanceof HTMLElement
      ? overflowItem
      : null;
  }

  const menuItem = listItem.querySelector(
    ':scope > [role="menuitem"]'
  );

  return menuItem instanceof HTMLElement
    ? menuItem
    : null;
}

/**
 * Find the submenu associated with a menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');

  if (!listItem) return null;

  const submenu = listItem.querySelector(
    ':scope > [ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}

/**
 * Get visible focusable elements inside a container.
 *
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
    )
  ).filter((element) => {
    if (!(element instanceof HTMLElement)) return false;

    return (
      !element.hasAttribute('hidden') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.offsetParent !== null
    );
  });
}